import {DuplicateIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useCallback} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {useUnmarkIssueAsDuplicateMutation} from '../mutations/unmark-issue-as-duplicate-mutation'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {MarkedAsDuplicateEvent$key} from './__generated__/MarkedAsDuplicateEvent.graphql'
import type {IssueLinkFragment} from './IssueLink'
import {IssueLink} from './IssueLink'
import {TimelineRow} from './row/TimelineRow'

type MarkedAsDuplicateEventProps = {
  queryRef: MarkedAsDuplicateEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  currentIssueId: string
  repositoryId: string
  ownerLogin: string
}

export function MarkedAsDuplicateEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  currentIssueId,
  repositoryId,
  ownerLogin,
}: MarkedAsDuplicateEventProps): React.ReactElement {
  const {
    actor,
    createdAt,
    canonical,
    duplicate,
    isCanonicalOfClosedDuplicate: isCanonicalDuplicate,
    databaseId,
    viewerCanUndo,
  } = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment MarkedAsDuplicateEvent on MarkedAsDuplicateEvent {
        actor {
          avatarUrl(size: 64)
          login
          profileResourcePath
          __typename
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
          ... on User {
            name
          }
          ... on EnterpriseUserAccount {
            name
          }
          ... on Mannequin {
            name
          }
        }
        createdAt
        duplicate {
          ... on Issue {
            __typename
            id
            titleHTML
            url
            number
            stateReason
            repository {
              id
              name
              isPrivate
              owner {
                login
              }
            }
          }
          ... on PullRequest {
            __typename
            id
            pullTitleHTML: titleHTML
            url
            number
            state
            isDraft
            isInMergeQueue
            repository {
              id
              name
              isPrivate
              owner {
                login
              }
            }
          }
        }
        canonical {
          ... on Issue {
            __typename
            id
            titleHTML
            url
            number
            stateReason
            repository {
              id
              name
              isPrivate
              owner {
                login
              }
            }
          }
          ... on PullRequest {
            __typename
            id
            pullTitleHTML: titleHTML
            url
            number
            state
            isDraft
            isInMergeQueue
            repository {
              id
              name
              isPrivate
              owner {
                login
              }
            }
          }
        }
        isCanonicalOfClosedDuplicate
        databaseId
        viewerCanUndo
      }
    `,
    queryRef,
  )

  const {
    mutate: unmarkIssueAsDuplicate,
    isPending: isUndoPending,
    isSuccess: isUndoComplete,
  } = useUnmarkIssueAsDuplicateMutation({issueUrl})

  const canonicalId =
    canonical?.__typename === 'Issue' || canonical?.__typename === 'PullRequest' ? canonical.id : undefined

  const unMarkAsDuplicate = useCallback(() => {
    if (!canonicalId) return

    const input = {duplicateId: currentIssueId, canonicalId}
    unmarkIssueAsDuplicate({input})
  }, [canonicalId, currentIssueId, unmarkIssueAsDuplicate])

  const highlighted = String(databaseId) === highlightedEventId

  if (!duplicate) return <></>

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={DuplicateIcon}
      fillRow={viewerCanUndo}
    >
      <TimelineRow.Main>
        {isCanonicalDuplicate ? (
          <>
            marked{' '}
            {/* RELAY-CLEANUP(MarkedAsDuplicateEvent): boundary cast; remove when MarkedAsDuplicateEvent leaves Relay. */}
            <IssueLink
              inline
              data={duplicate as IssueLinkFragment}
              targetRepositoryId={repositoryId}
              targetOwnerLogin={ownerLogin}
            />{' '}
            as a duplicate of this issue{' '}
          </>
        ) : (
          <>
            {LABELS.timeline.markedAsDuplicate}
            <>
              &nbsp;
              {/* RELAY-CLEANUP(MarkedAsDuplicateEvent): boundary cast; remove when MarkedAsDuplicateEvent leaves Relay. */}
              <IssueLink
                inline
                data={duplicate as IssueLinkFragment}
                targetRepositoryId={repositoryId}
                targetOwnerLogin={ownerLogin}
              />{' '}
            </>
          </>
        )}
      </TimelineRow.Main>
      {viewerCanUndo &&
      !isUndoPending &&
      !isUndoComplete &&
      (duplicate?.__typename === 'Issue' || duplicate?.__typename === 'PullRequest') &&
      duplicate.number &&
      !isCanonicalDuplicate ? (
        <TimelineRow.Trailing>
          <Button onClick={unMarkAsDuplicate} aria-label={LABELS.undoMarkIssueAsDuplicate(duplicate.number)}>
            Undo
          </Button>
        </TimelineRow.Trailing>
      ) : null}
    </TimelineRow>
  )
}
